import { io, Socket } from "socket.io-client";
import type { AppDispatch } from "@/redux/store";
import { addVisitor, updateVisitor, markVisitorInactive } from "@/redux/slices/visitorsSlice";
import { addCall, updateCall, setActiveCallSession } from "@/redux/slices/callsSlice";
import { addCallRequest, removeCallRequest } from "@/redux/slices/callRequestsSlice";
import { updateMemberStatus } from "@/redux/slices/teamSlice";
import {
  addNotification,
  removeNotification,
  setUnreadCount,
  type Notification,
} from "@/redux/slices/notificationsSlice";
import { API_URL } from "@/lib/services/api.service";

/** Verbose socket tracing — keep production consoles clean. */
function socketDbg(...args: unknown[]) {
  if (import.meta.env.DEV) console.log(...args);
}

export class DashboardSocketService {
  private socket: Socket | null = null;
  private dispatch: AppDispatch;
  private apiUrl: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private currentUserId: string | null = null;
  private activeCallId: string | null = null;
  private activeRoom: { url: string; name?: string } | null = null;
  private activeToken: string | null = null;
  private notificationAudio: HTMLAudioElement | null = null;
  private callAudio: HTMLAudioElement | null = null;

  constructor(dispatch: AppDispatch, apiUrl: string = API_URL) {
    this.dispatch = dispatch;
    this.apiUrl = apiUrl;
    // Initialize notification sounds
    this.notificationAudio = new Audio("/assets/sounds/notification-tone.mp3");
    this.notificationAudio.preload = "auto";

    this.callAudio = new Audio("/assets/sounds/ring.mp3");
    this.callAudio.preload = "auto";
  }

  connect(userId: string, token: string) {
    if (this.socket?.connected) {
      socketDbg("[Socket] Already connected");
      return;
    }

    this.currentUserId = userId;
    socketDbg("[Socket] Connecting to:", this.apiUrl);

    this.socket = io(`${this.apiUrl}/dashboard`, {
      auth: {
        token,
        userId,
      },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
    });

    this.setupEventListeners();
  }

  private setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on("connect", () => {
      socketDbg("[Socket] ✓ Connected! Socket ID:", this.socket?.id);
      this.reconnectAttempts = 0;

      // Request initial data when connected
      if (this.socket) {
        socketDbg("[Socket] Requesting initial dashboard data");
        this.socket.emit("dashboard:init");
      }
    });

    this.socket.on("connect_error", (error) => {
      console.error("[Socket] Connection error:", error);
    });

    // Initial data received
    this.socket.on("dashboard:initialized", (data: any) => {
      socketDbg("[Socket] Dashboard initialized:", data);

      // Create a Set of active visitor IDs for quick lookup
      const activeVisitorIds = new Set<string>();
      if (data.activeVisitors && Array.isArray(data.activeVisitors)) {
        data.activeVisitors.forEach((visitor: any) => {
          if (visitor?.id) {
            activeVisitorIds.add(visitor.id);
          }
        });
      }

      // Add all visitors (not just active ones) so reps can see them on login/refresh
      // But mark them as active/inactive based on the activeVisitors array
      if (data.visitors && Array.isArray(data.visitors)) {
        socketDbg(`[Socket] Adding ${data.visitors.length} visitors from initialization`);
        data.visitors.forEach((visitor: any) => {
          const normalized = this.normalizeVisitorPayload(visitor);
          if (normalized && normalized.id) {
            // Override is_active based on whether visitor is in activeVisitors array
            normalized.is_active = activeVisitorIds.has(normalized.id);
            this.dispatch(addVisitor(normalized));
          }
        });
      }

      // Add calls and check if user has an ongoing call to rejoin
      if (data.ongoingCalls) {
        data.ongoingCalls.forEach((call: any) => {
          const normalizedCall = this.normalizeCallPayload(call);
          if (normalizedCall) {
            this.dispatch(addCall(normalizedCall as any));

            // Check if this call belongs to the current user and they can rejoin
            if (
              normalizedCall.sales_rep_id === this.currentUserId &&
              normalizedCall.daily_room_url
            ) {
              socketDbg("[Socket] Found ongoing call for current user on reconnect:", {
                callId: normalizedCall.id,
                status: normalizedCall.status,
                roomUrl: normalizedCall.daily_room_url,
              });

              // Request rejoin token from backend
              if (this.socket) {
                this.socket.emit("call:request-rejoin", { callId: normalizedCall.id });
              }
            }
          }
        });
      }

      // Add call requests (pending calls waiting for visitor acceptance)
      if (data.callRequests) {
        data.callRequests.forEach((request: any) => {
          this.dispatch(addCallRequest(request));
        });
      }
    });

    this.socket.on("disconnect", (reason) => {
      socketDbg("[Socket] Disconnected:", reason);
    });

    this.socket.on("connect_error", (error: any) => {
      console.error("[Socket] Connection error:", error);
      console.error("[Socket] Connection error details:", {
        message: error?.message || "Unknown error",
        type: error?.type || "unknown",
        description: error?.description || "No description",
      });
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error("[Socket] Max reconnection attempts reached");
      }
    });

    // Visitor events
    this.socket.on("visitor:new", (payload: any) => {
      socketDbg("[Socket] New visitor:", payload);
      // Handle both {visitor: {...}} and direct visitor object
      const visitor = payload.visitor || payload;
      const normalized = this.normalizeVisitorPayload(visitor, { defaultIsActive: true });
      if (normalized && normalized.id) {
        this.dispatch(addVisitor(normalized));
      }
    });

    this.socket.on("visitor:updated", (payload: any) => {
      socketDbg("[Socket] Visitor updated:", payload);
      // Handle both {visitor: {...}} and direct visitor object
      const visitor = payload.visitor || payload;
      const normalized = this.normalizeVisitorPayload(visitor, { defaultIsActive: true });
      if (normalized && normalized.id) {
        this.dispatch(updateVisitor(normalized));
      }
    });

    this.socket.on("visitor:left", (visitorId: string) => {
      socketDbg("[Socket] Visitor left:", visitorId);
      // Do not delete the visitor record — just mark inactive so totals/history remain consistent.
      this.dispatch(markVisitorInactive({ id: visitorId, last_seen: new Date().toISOString() }));
    });

    this.socket.on("visitor:activity", (data: any) => {
      socketDbg("[Socket] Visitor activity:", data);
      // Update visitor with new activity
      const visitor = data.visitor || data;
      const normalized = this.normalizeVisitorPayload(visitor, { defaultIsActive: true });
      if (normalized && normalized.id) {
        this.dispatch(updateVisitor(normalized));
      }
    });

    // Call events
    this.socket.on("call:initiated", (payload: any) => {
      socketDbg("[Socket] ✓✓✓ Call initiated event received:", {
        payload,
        salesRepId: payload?.salesRepId,
        currentUserId: this.currentUserId,
        hasRoom: !!payload?.room,
        hasToken: !!payload?.salesRepToken,
      });

      const callPayload = this.normalizeCallPayload(payload?.call || payload);
      if (callPayload) {
        callPayload.room = payload?.room || null;
        callPayload.salesRepToken = payload?.salesRepToken || null;
      }

      // Remove call request if this call was initiated from a request
      if (callPayload?.visitor_id) {
        this.dispatch(removeCallRequest(callPayload.visitor_id));
      }

      if (callPayload) {
        this.dispatch(addCall(callPayload as any));
      }

      // Set active call session if this is the current user's call
      if (payload?.salesRepId && payload.salesRepId === this.currentUserId && callPayload) {
        this.activeCallId = callPayload.id;
        this.activeRoom = payload?.room || { url: payload?.roomUrl || callPayload.daily_room_url };
        this.activeToken = payload?.salesRepToken || payload?.token || null;

        socketDbg("[Socket] ✓✓✓ Setting active call session from call:initiated:", {
          callId: callPayload.id,
          callStatus: callPayload.status,
          roomUrl: this.activeRoom?.url,
          roomName: this.activeRoom?.name,
          hasToken: !!this.activeToken,
          salesRepId: payload.salesRepId,
          currentUserId: this.currentUserId,
        });

        this.dispatch(
          setActiveCallSession({
            call: callPayload as any,
            room: this.activeRoom || undefined,
            token: this.activeToken || undefined,
          }),
        );
      } else {
        socketDbg("[Socket] Call not for current user:", {
          salesRepId: payload?.salesRepId,
          currentUserId: this.currentUserId,
          hasCallPayload: !!callPayload,
        });
      }
    });

    this.socket.on("call:accepted", (payload: any) => {
      socketDbg("[Socket] ✓✓✓ Call accepted event received:", {
        payload,
        callId: payload?.call?.id,
        salesRepId: payload?.call?.sales_rep_id,
        currentUserId: this.currentUserId,
        userIdsMatch: payload?.call?.sales_rep_id === this.currentUserId,
        hasRoom: !!payload?.room,
        hasToken: !!payload?.salesRepToken,
      });

      const call = this.normalizeCallPayload(payload?.call || payload);
      if (call) {
        this.dispatch(updateCall(call as any));

        // Update active call session if this is the current user's call
        // Use room and token from payload if available
        if (call.sales_rep_id === this.currentUserId) {
          this.activeCallId = call.id;
          this.activeRoom = payload?.room || { url: call.daily_room_url };
          this.activeToken = payload?.salesRepToken || payload?.token || null;

          socketDbg("[Socket] ✓✓✓ Setting active call session from call:accepted:", {
            callId: call.id,
            callStatus: call.status,
            roomUrl: this.activeRoom?.url,
            roomName: this.activeRoom?.name,
            hasToken: !!this.activeToken,
            salesRepId: call.sales_rep_id,
            currentUserId: this.currentUserId,
          });

          this.dispatch(
            setActiveCallSession({
              call: call as any,
              room: this.activeRoom || undefined,
              token: this.activeToken || undefined,
            }),
          );
        } else if (this.activeCallId === call.id) {
          // Fallback: if activeCallId matches but sales_rep_id doesn't, still update with available data
          socketDbg("[Socket] Updating active call session (fallback):", {
            callId: call.id,
            hasRoom: !!payload?.room,
            hasToken: !!payload?.salesRepToken,
          });

          this.dispatch(
            setActiveCallSession({
              call: call as any,
              room: payload?.room || this.activeRoom || undefined,
              token: payload?.salesRepToken || payload?.token || this.activeToken || undefined,
            }),
          );
        } else {
          socketDbg("[Socket] Call accepted but not for current user:", {
            callSalesRepId: call.sales_rep_id,
            currentUserId: this.currentUserId,
            activeCallId: this.activeCallId,
          });
        }
      }
    });

    this.socket.on("call:rejected", (payload: any) => {
      socketDbg("[Socket] Call rejected:", payload);
      const call = this.normalizeCallPayload(payload?.call || payload);
      if (call) {
        // Stop notification sound
        this.stopNotificationSound();

        // Remove from call requests
        this.dispatch(removeCallRequest(call.visitor_id));
        // Update call status
        this.dispatch(updateCall(call as any));
        if (this.activeCallId === call.id) {
          this.activeCallId = null;
          this.activeRoom = null;
          this.activeToken = null;
          this.dispatch(setActiveCallSession({ call: null }));
        }
      }
    });

    this.socket.on("call:ended", (payload: any) => {
      socketDbg("[Socket] Call ended:", payload);
      const call = this.normalizeCallPayload(payload?.call || payload);
      if (call) {
        // Stop notification sound
        this.stopNotificationSound();

        // Remove from call requests
        this.dispatch(removeCallRequest(call.visitor_id));
        // Update call status
        this.dispatch(updateCall(call as any));
        if (this.activeCallId === call.id) {
          this.activeCallId = null;
          this.activeRoom = null;
          this.activeToken = null;
          this.dispatch(setActiveCallSession({ call: null }));
        }
      }
    });

    this.socket.on("call:missed", (payload: any) => {
      socketDbg("[Socket] Call missed:", payload);
      const call = this.normalizeCallPayload(payload?.call || payload);
      if (call) {
        // Stop notification sound
        this.stopNotificationSound();

        // Remove from call requests
        this.dispatch(removeCallRequest(call.visitor_id));
        // Update call status
        this.dispatch(updateCall(call as any));
        if (this.activeCallId === call.id) {
          this.activeCallId = null;
          this.activeRoom = null;
          this.activeToken = null;
          this.dispatch(setActiveCallSession({ call: null }));
        }
      }
    });

    this.socket.on("visitor:call-requested", (request: any) => {
      socketDbg("[Socket] Call requested:", request);
      const normalizedRequest = {
        requestId: request.requestId || `${request.visitorId}-${request.requestedAt || Date.now()}`,
        ...request,
      };
      this.dispatch(addCallRequest(normalizedRequest));

      // Play notification sound for incoming call request
      this.playNotificationSound("call", true); // Loop sound for call request
    });

    this.socket.on("call:cancelled", (payload: any) => {
      socketDbg("[Socket] Call cancelled by visitor:", payload);
      const call = this.normalizeCallPayload(payload?.call || payload);
      if (call) {
        // Stop notification sound when visitor cancels
        this.stopNotificationSound();

        // Remove from call requests if it exists there (removeCallRequest expects visitorId)
        this.dispatch(removeCallRequest(call.visitor_id));
        // Update call status
        this.dispatch(updateCall(call as any));
        // Clear active call if this was it
        if (this.activeCallId === call.id) {
          this.activeCallId = null;
          this.activeRoom = null;
          this.activeToken = null;
          this.dispatch(setActiveCallSession({ call: null }));
        }
      }
    });

    this.socket.on("call:rejoin-ready", (payload: any) => {
      socketDbg("[Socket] ✓✓✓ Call rejoin ready:", payload);

      const callPayload = this.normalizeCallPayload(payload?.call || payload);
      if (callPayload && payload?.token && payload?.room) {
        // Set active call session to rejoin
        this.activeCallId = callPayload.id;
        this.activeRoom = payload.room;
        this.activeToken = payload.token;

        socketDbg("[Socket] ✓✓✓ Setting active call session for rejoin:", {
          callId: callPayload.id,
          roomUrl: this.activeRoom?.url,
          hasToken: !!this.activeToken,
        });

        this.dispatch(
          setActiveCallSession({
            call: callPayload as any,
            room: this.activeRoom,
            token: this.activeToken,
          }),
        );
      }
    });

    // User status events
    this.socket.on(
      "user:status-updated",
      (data: { userId: string; status: "online" | "offline" | "busy" }) => {
        socketDbg("[Socket] User status updated:", data);
        this.dispatch(updateMemberStatus(data));
      },
    );

    // ==============================================================
    // Notification Events
    // ==============================================================

    // New notification received
    this.socket.on("notification:new", (notification: Notification) => {
      socketDbg("[Socket] New notification received:", notification);

      // Play notification sound for non-system notifications
      if (notification.type !== "SYSTEM") {
        const soundType = notification.type === "CALL" ? "call" : "notification";
        this.playNotificationSound(soundType, false, 0.3);
      }

      // Dispatch to notification store
      this.dispatch(addNotification(notification));
    });

    // Notification deleted
    this.socket.on("notification:deleted", (data: { id: string }) => {
      socketDbg("[Socket] Notification deleted:", data);
      this.dispatch(removeNotification(data.id));
    });

    // Unread count updated
    this.socket.on("notification:unread-count", (data: { count: number }) => {
      socketDbg("[Socket] Unread notification count updated:", data);
      this.dispatch(setUnreadCount(data.count));
    });

    // Error handling
    this.socket.on("error", (error: any) => {
      console.error("[Socket] Error:", error);
    });
  }

  /**
   * Request fresh dashboard data (visitors, ongoing calls, call requests).
   * Safe to call when already connected; backend will reply with dashboard:initialized.
   */
  requestDashboardInit() {
    if (this.socket?.connected) {
      this.socket.emit("dashboard:init");
    }
  }

  // Emit events
  initiateCall(visitorId: string, callType: "video" | "voice") {
    if (!this.socket?.connected) {
      console.error("[Socket] Not connected, cannot initiate call");
      throw new Error("Socket not connected. Please refresh and try again.");
    }

    socketDbg("[Socket] Initiating call:", { visitorId, callType });
    this.socket.emit("call:initiate", { visitorId, callType });
  }

  /**
   * Answer a visitor's call request by joining their existing room.
   * This is used when a visitor requests a call (creates room) and rep accepts.
   */
  answerCallRequest(request: {
    callId: string;
    roomUrl: string;
    roomName?: string;
    type: "video" | "voice";
    visitorId: string;
  }) {
    if (!this.socket?.connected) {
      console.error("[Socket] Not connected, cannot answer call request");
      throw new Error("Socket not connected. Please refresh and try again.");
    }

    socketDbg("[Socket] Answering visitor call request:", {
      callId: request.callId,
      roomUrl: request.roomUrl,
      type: request.type,
      visitorId: request.visitorId,
    });

    // Emit to backend that we're answering this request
    this.socket.emit("call:answer-request", {
      callId: request.callId,
      visitorId: request.visitorId,
      callType: request.type,
    });

    // The backend will respond with call:started containing the token
    // and we'll use the roomUrl from the request to join
  }

  private normalizeCallPayload(call: any) {
    if (!call) return null;
    return {
      ...call,
      call_type: call.call_type || (call.type ? call.type.toLowerCase() : undefined),
      status: call.status ? call.status.toLowerCase() : call.status,
    };
  }

  private normalizeVisitorPayload(visitor: any, opts?: { defaultIsActive?: boolean }) {
    if (!visitor) return null;

    const hasIsActiveField =
      Object.prototype.hasOwnProperty.call(visitor, "is_active") ||
      Object.prototype.hasOwnProperty.call(visitor, "isActive");

    // Normalize visitor data to match Redux Visitor interface
    const meta = visitor.metadata && typeof visitor.metadata === "object" ? visitor.metadata : {};
    return {
      id: visitor.id,
      session_id: visitor.session_id,
      widget_id: visitor.widget_id || visitor.widgetId || "",
      page_url: visitor.page_url || visitor.current_url || visitor.pageUrl || "",
      page_title: visitor.page_title || visitor.pageTitle || "Untitled",
      referrer: visitor.referrer || undefined,
      ip_address: visitor.ip_address || visitor.ipAddress || undefined,
      user_agent: visitor.user_agent || visitor.userAgent || undefined,
      country: visitor.country || undefined,
      city: visitor.city || undefined,
      name: visitor.name ?? meta.name ?? undefined,
      email: visitor.email ?? meta.email ?? undefined,
      phone: visitor.phone ?? meta.phone ?? undefined,
      metadata: visitor.metadata || {},
      first_seen:
        visitor.first_seen ||
        visitor.firstSeen ||
        visitor.last_seen ||
        visitor.lastSeen ||
        new Date().toISOString(),
      last_seen: visitor.last_seen || visitor.lastSeen || new Date().toISOString(),
      is_active: hasIsActiveField
        ? visitor.is_active !== undefined
          ? visitor.is_active
          : visitor.isActive !== undefined
            ? visitor.isActive
            : false
        : (opts?.defaultIsActive ?? false),
      total_page_views:
        visitor.total_page_views ||
        visitor.totalPageViews ||
        visitor.page_views ||
        visitor.pageViews ||
        1,
      time_on_site: visitor.time_on_site || visitor.timeOnSite || 0,
    };
  }

  endCall(callId: string) {
    if (!this.socket?.connected) {
      console.error("[Socket] Not connected");
      return;
    }

    this.socket.emit("call:end", { callId });
  }

  updateUserStatus(status: "online" | "offline" | "busy") {
    if (!this.socket?.connected) {
      console.error("[Socket] Not connected");
      return;
    }

    this.socket.emit("user:status", { status });
  }

  // Play notification sound (for call requests and notifications)
  playNotificationSound(type: "notification" | "call", loop = false, volume = 0.5) {
    try {
      const audio = type === "call" ? this.callAudio : this.notificationAudio;

      if (!audio) {
        console.warn(`[Socket] ${type} audio not initialized`);
        return;
      }

      audio.currentTime = 0;
      audio.loop = loop;
      audio.volume = Math.max(0, Math.min(1, volume));

      audio.play().catch((error) => {
        console.warn(`[Socket] Autoplay prevented for ${type}:`, error);
        // Autoplay blocked - user needs to interact with page first
      });
    } catch (error) {
      console.error(`[Socket] Error playing ${type} sound:`, error);
    }
  }

  // Stop notification sound
  stopNotificationSound(type?: "notification" | "call") {
    try {
      if (type === "call" || type === undefined) {
        if (this.callAudio) {
          this.callAudio.pause();
          this.callAudio.currentTime = 0;
        }
      }

      if (type === "notification" || type === undefined) {
        if (this.notificationAudio) {
          this.notificationAudio.pause();
          this.notificationAudio.currentTime = 0;
        }
      }
    } catch (error) {
      console.error("[Socket] Error stopping notification sound:", error);
    }
  }

  // Disconnect
  disconnect() {
    if (this.socket) {
      socketDbg("[Socket] Disconnecting...");
      this.socket.disconnect();
      this.socket = null;
    }

    // Clean up audio
    this.stopNotificationSound();
    if (this.notificationAudio) {
      this.notificationAudio.src = "";
      this.notificationAudio = null;
    }
    if (this.callAudio) {
      this.callAudio.src = "";
      this.callAudio = null;
    }
  }

  // Get connection status
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

// Singleton instance
let socketService: DashboardSocketService | null = null;

export const initSocketService = (
  dispatch: AppDispatch,
  apiUrl?: string,
): DashboardSocketService => {
  if (!socketService) {
    socketService = new DashboardSocketService(dispatch, apiUrl);
  }
  return socketService;
};

export const getSocketService = (): DashboardSocketService | null => {
  return socketService;
};

export const disconnectSocketService = () => {
  if (socketService) {
    socketService.disconnect();
    socketService = null;
  }
};
